import cookies from "react-cookies";
import { order } from "../../../types";
import { backend } from "../../../config/environment";

type WebSocketMessage = {
  type: "order" | "pong" | string;
  data: typeof order.order;
};

type ConnectionState = "connecting" | "connected" | "disconnected";

const MAX_RETRIES = 10;
const INITIAL_RETRY_DELAY = 1000;
const MAX_RETRY_DELAY = 30000;
const HEARTBEAT_INTERVAL = 210_000;

export const connectWebSocket = (
  callback: (message: typeof order.order) => void,
  onStateChange?: (state: ConnectionState) => void,
): (() => void) => {
  let ws: WebSocket | null = null;
  let heartbeatInterval: NodeJS.Timeout | null = null;
  let reconnectTimeout: NodeJS.Timeout | null = null;
  let retryCount = 0;
  let isCleanedUp = false;

  const clearTimers = () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }
  };

  const getRetryDelay = (): number => {
    const delay = Math.min(
      INITIAL_RETRY_DELAY * Math.pow(2, retryCount),
      MAX_RETRY_DELAY,
    );
    return delay;
  };

  const connect = () => {
    if (isCleanedUp) return;

    const userToken = cookies.load("token");
    if (!userToken) {
      console.error("Token de usuario no encontrado.");
      onStateChange?.("disconnected");
      return;
    }

    onStateChange?.("connecting");

    try {
      ws = new WebSocket(`${backend.WS_URI}/?token=${userToken}`);
    } catch (err) {
      console.error("Error creando WebSocket:", err);
      scheduleReconnect();
      return;
    }

    ws.onopen = () => {
      console.log("Conexión WebSocket establecida.");
      retryCount = 0;
      onStateChange?.("connected");

      heartbeatInterval = setInterval(() => {
        if (ws?.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping",
            t: Date.now() }));
          console.log("Ping enviado");
        }
      }, HEARTBEAT_INTERVAL);
    };

    ws.onmessage = (event) => {
      try {
        const data: WebSocketMessage = JSON.parse(event.data);

        if (data.type === "pong") {
          console.log("Pong recibido");
          return;
        }

        console.log("Mensaje recibido:", data);
        callback(data.data);
      } catch (err) {
        console.error("Error parseando mensaje:", err);
      }
    };

    ws.onclose = (event) => {
      console.log("Conexión WebSocket cerrada.", event.code, event.reason);
      clearTimers();
      onStateChange?.("disconnected");
      scheduleReconnect();
    };

    ws.onerror = (error) => {
      console.error("Error en WebSocket:", error);
      clearTimers();
    };
  };

  const scheduleReconnect = () => {
    if (isCleanedUp) return;

    if (retryCount >= MAX_RETRIES) {
      console.error(
        `Máximo de reintentos alcanzado (${MAX_RETRIES}). Deteniendo reconexión.`,
      );
      onStateChange?.("disconnected");
      return;
    }

    const delay = getRetryDelay();
    retryCount++;
    console.log(
      `Reintentando conexión en ${delay / 1000}s (intento ${retryCount}/${MAX_RETRIES})`,
    );

    reconnectTimeout = setTimeout(() => {
      connect();
    }, delay);
  };

  connect();

  return () => {
    isCleanedUp = true;
    clearTimers();
    if (ws) {
      ws.onclose = null;
      ws.onerror = null;
      ws.close();
      ws = null;
    }
  };
};
